import { memo } from 'react';
import isUndefined from 'lodash/isUndefined';
import get from 'lodash/get';
import { Icon, Skeleton, Tooltip, Text } from '@cronos-labs/tectonic-ui-kit';
import clsx from 'clsx';

import { PARTNER_TOKEN_ICONS } from '@config/constants';
import { formatPercent } from '@lib/units';

interface NetApyCellProps {
  assetType: 'Borrow' | 'Supply';
  netApy: Undefined<number>;
  apy: Undefined<number>;
  tonicApy: number;
  netPartnerTokenApy: number;
  partnerTokenApy: Record<string, number>;
  isLoading: boolean;
  className?: string;
  skeletonWidth?: 'small' | 'medium' | 'large';
}

function NetApyCell({
  assetType,
  netApy,
  apy,
  tonicApy,
  netPartnerTokenApy,
  partnerTokenApy,
  isLoading,
  className,
  skeletonWidth = 'small',
}: NetApyCellProps) {
  const skeletonWidthClass = clsx('h-3', {
    'w-5': skeletonWidth === 'small',
    'w-10': skeletonWidth === 'medium',
    'w-[126px]': skeletonWidth === 'large',
  });

  const symbol = assetType === 'Borrow' ? '-' : '+';

  return (
    <div className={clsx('flex flex-col items-end', className)}>
      <div className="flex justify-end text-small text-white60">
        {!isUndefined(netApy) && !isLoading ? (
          <div
            className="flex items-center gap-0.5"
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            <Text
              className={clsx({
                'text-vaultChart12Months': netApy < 0,
              })}
            >
              {formatPercent(netApy)}
            </Text>
            <Tooltip
              leftOffset={100}
              message={
                <Text className="whitespace-nowrap text-small">
                  {formatPercent(apy ?? 0)} {assetType} APY {symbol}{' '}
                  {formatPercent(tonicApy * 100)} TONIC rewards
                  {netPartnerTokenApy > 0
                    ? `${symbol} ${formatPercent(
                        netPartnerTokenApy * 100
                      )} non-TONIC rewards`
                    : ''}
                </Text>
              }
            >
              <Icon.WhiteQuestion width={24} height={24} />
            </Tooltip>
          </div>
        ) : (
          <Skeleton className={skeletonWidthClass} />
        )}
      </div>

      <div
        className={clsx('flex gap-1', {
          'mr-0.5': !isLoading,
          'mt-0.5': isLoading,
        })}
      >
        {!isLoading ? (
          <>
            {!!tonicApy && <PARTNER_TOKEN_ICONS.TONIC />}
            {Object.keys(partnerTokenApy).map((tokenSymbol) => {
              const PartnerIcon = get(
                PARTNER_TOKEN_ICONS,
                tokenSymbol.toUpperCase(),
                null
              );

              if (
                !PartnerIcon ||
                netPartnerTokenApy === 0 ||
                partnerTokenApy[tokenSymbol] === 0
              )
                return null;

              return <PartnerIcon key={tokenSymbol} />;
            })}
          </>
        ) : (
          <Skeleton className={skeletonWidthClass} />
        )}
      </div>
    </div>
  );
}

export default memo(NetApyCell, (prev, next) => {
  return (
    prev.assetType === next.assetType &&
    prev.netApy === next.netApy &&
    prev.apy === next.apy &&
    prev.tonicApy === next.tonicApy &&
    prev.netPartnerTokenApy === next.netPartnerTokenApy &&
    prev.partnerTokenApy === next.partnerTokenApy &&
    prev.isLoading === next.isLoading &&
    prev.className === next.className &&
    prev.skeletonWidth === next.skeletonWidth
  );
});
